import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Loader2, RefreshCw, Sparkles } from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ChannelPicker } from '@/components/onboarding/ChannelPicker'
import { ChangesBar } from '@/components/onboarding/ChangesBar'
import { AddChannelDialog } from '@/components/AddChannelDialog'
import { useUnsavedGuard } from '@/hooks/use-unsaved-guard'

function diffSets(selected: Set<string>, original: Set<string>) {
  const toFollow: string[] = []
  const toUnfollow: string[] = []
  for (const id of selected) if (!original.has(id)) toFollow.push(id)
  for (const id of original) if (!selected.has(id)) toUnfollow.push(id)
  return { toFollow, toUnfollow }
}

export function ChannelsPage() {
  const [searchParams] = useSearchParams()
  // First-run redirect from sign-in: the backend is importing subscriptions and
  // auto-following the top channel in the background. Show a welcome banner and
  // poll until those channels land (rather than flashing the empty state).
  const isWelcome = searchParams.get('welcome') === '1'
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [original, setOriginal] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [settingUp, setSettingUp] = useState(false)
  const [saving, setSaving] = useState(false)
  const [hasAnyImported, setHasAnyImported] = useState<boolean | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null)
  // Bumped to remount the (internally-paginated) picker after adding a channel
  // by URL so the new row shows up immediately.
  const [pickerKey, setPickerKey] = useState(0)

  const handleChannelAdded = (channel: { id: string; title: string }) => {
    // The backend already activated the follow — reflect it in both the saved
    // baseline and the current selection so it shows as followed, not pending.
    setOriginal((prev) => new Set(prev).add(channel.id))
    setSelected((prev) => new Set(prev).add(channel.id))
    setHasAnyImported(true)
    setRefreshMessage(`Now tracking ${channel.title}.`)
    setPickerKey((k) => k + 1)
  }

  const loadFollowed = async () => {
    const { ids } = await api.getFollowedChannelIds()
    const next = new Set(ids)
    setOriginal(next)
    setSelected(new Set(next))
    return ids.length
  }

  const handleRefreshFromYouTube = async () => {
    setRefreshing(true)
    setRefreshMessage(null)
    try {
      const before = await api.getChannels({ pageSize: 1 })
      await api.importSubscriptions()
      const after = await api.getChannels({ pageSize: 1 })
      const added = Math.max(0, after.total - before.total)
      setRefreshMessage(added > 0 ? `Added ${added} new channel${added === 1 ? '' : 's'} from YouTube.` : 'You\u2019re up to date with YouTube.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Refresh failed.'
      setRefreshMessage(msg)
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    // ~40s ceiling (20 × 2s) before we give up polling and fall back to the
    // manual empty-state CTA — covers a slow import without hanging forever.
    const MAX_ATTEMPTS = 20

    // Cheap probe: refreshes the followed baseline (so the auto-followed
    // channel shows as checked) and reports whether any channels are imported.
    // `ready` gates the welcome poll on the auto-follow having actually landed.
    const probe = async () => {
      const [followedCount, res] = await Promise.all([loadFollowed(), api.getChannels({ pageSize: 1 })])
      const imported = res.total > 0
      if (!cancelled) setHasAnyImported(imported)
      return { imported, ready: followedCount > 0 }
    }

    const run = async () => {
      try {
        let { ready } = await probe()
        // On the welcome redirect the import + auto-follow are still in flight —
        // poll until the first follow lands, showing the "setting up" state.
        for (let i = 0; isWelcome && !ready && !cancelled && i < MAX_ATTEMPTS; i++) {
          setLoading(false)
          setSettingUp(true)
          await new Promise((r) => setTimeout(r, 2000))
          if (cancelled) return
          ;({ ready } = await probe())
        }
      } catch (err) {
        console.error(err)
      } finally {
        if (!cancelled) {
          setLoading(false)
          setSettingUp(false)
        }
      }
    }

    setLoading(true)
    run()
    return () => {
      cancelled = true
    }
  }, [isWelcome])

  const { toFollow, toUnfollow } = useMemo(() => diffSets(selected, original), [selected, original])
  const dirty = toFollow.length + toUnfollow.length > 0

  useUnsavedGuard(dirty)

  const handleSave = async () => {
    setSaving(true)
    try {
      if (toFollow.length > 0) await api.followChannels(toFollow)
      if (toUnfollow.length > 0) await api.unfollowChannels(toUnfollow)
      // Re-seed original from server to confirm new baseline.
      await loadFollowed()
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handleDiscard = () => {
    setSelected(new Set(original))
  }

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-48" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-xl" />
        ))}
      </div>
    )
  }

  if (settingUp) {
    return (
      <Card className="p-12 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
        <p className="mt-4 font-medium">Setting up your channels…</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Importing your YouTube subscriptions. This only takes a moment.
        </p>
      </Card>
    )
  }

  if (hasAnyImported === false) {
    return (
      <Card className="p-12 text-center">
        <p className="text-muted-foreground">No channels yet.</p>
        <div className="mt-4 flex items-center justify-center gap-2">
          <Link to="/onboarding">
            <Button>Import subscriptions</Button>
          </Link>
          <AddChannelDialog
            onAdded={handleChannelAdded}
            trigger={<Button variant="outline">Add a channel by URL</Button>}
          />
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-5 pb-24">
      {isWelcome && (
        <Card className="flex items-start gap-3 border-primary/30 bg-primary/5 p-4">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="text-sm">
            <p className="font-medium">Welcome to Rundown 👋</p>
            <p className="mt-1 text-muted-foreground">
              We imported your YouTube subscriptions and started following your top channel so your
              feed isn’t empty. Pick the channels you actually want below, then hit Save — you can
              change these anytime.
            </p>
          </div>
        </Card>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Channels</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Following <span className="font-medium text-foreground">{selected.size}</span> channel
            {selected.size === 1 ? '' : 's'}. Toggle to add or remove, then Save.
          </p>
          {refreshMessage && (
            <p className="mt-2 text-xs text-muted-foreground">{refreshMessage}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <AddChannelDialog onAdded={handleChannelAdded} />
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshFromYouTube}
            disabled={refreshing}
            className="gap-1.5"
          >
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            {refreshing ? 'Refreshing\u2026' : 'Refresh from YouTube'}
          </Button>
        </div>
      </div>

      <ChannelPicker key={pickerKey} selected={selected} onSelectionChange={setSelected} />

      <ChangesBar
        toFollowCount={toFollow.length}
        toUnfollowCount={toUnfollow.length}
        saving={saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />
    </div>
  )
}
